import * as yup from "yup";

import { popupImportFormActionKindRawFromCsv } from "./convertFunctions";
import { POPUP_IMPORT_SEGMENT_ALL_VALUE } from "./popupImportGraphqlEnumCells";
import { ensurePopupImportClientRowIds } from "./popupImportRowIdentity";

/** Same defaults as `PopupGroupPatternCreatePageContainer` `initialValues` for enum-backed fields. */
export const POPUP_IMPORT_POPUP_PATTERN_ENUM_DEFAULTS = {
  colorKindRaw: "DEFAULT",
  hrefTargetRaw: "BLANK",
  displayKindRaw: "CENTER_MODAL",
  closePopupByClickingOutsideRaw: "DISABLED",
  animationKindRaw: "NO_ANIMATION",
} as const;

function popupImportEnumStringWithPatternDefault<
  K extends keyof typeof POPUP_IMPORT_POPUP_PATTERN_ENUM_DEFAULTS
>(key: K) {
  const fallback = POPUP_IMPORT_POPUP_PATTERN_ENUM_DEFAULTS[key];
  return yup
    .string()
    .transform((v: unknown) => {
      const s = (v ?? "").toString().trim();
      return s === "" ? fallback : s;
    })
    .default(fallback);
}

export type PopupImportImageForm = {
  imageUrl: string;
  honeycombCode: string;
  alt: string;
  linkUrl: string;
  isInheritQuery: boolean;
};

/** One row inside `DeliverDayOfWeeksFieldArray` (aligned with popup group form). */
export type PopupImportDeliverDayOfWeekRow = {
  weekOfMonth: number | null;
  daysOfWeek: string[];
};

export type PopupImportGroupForm = {
  name: string;
  memo: string;
  startDate: Date | null;
  endDate: Date | null;
  scheduleIds: string[];
  deliverDayOfWeeks: PopupImportDeliverDayOfWeekRow[];
  redisplayPopupTime: string;
  /** Segment enums; `[POPUP_IMPORT_SEGMENT_ALL_VALUE]` = 全て chip (omit on import API). Import-only. */
  devices: string[];
  platforms: string[];
  browsers: string[];
  genders: string[];
  ageRanges: string[];
  annualIncomes: string[];
  sourceIds: string[];
  excludeSourceIds: string[];
  referrers: { referrer: string; matchOp: string }[];
  excludeReferrers: { referrer: string; matchOp: string }[];
  visitCount: string;
  displayTargetContents: { targetKind: string; targetValue: string }[];
  disableTargetElements: { targetKind: string; targetValue: string }[];
  deliverTargetUrls: { url: string }[];
  deliverExcludeUrls: { url: string }[];
  targetCookies: { key: string; value: string; matchType: boolean }[];
  /** `"true"` / `"false"` — same string convention as `PopupGroupForm` `isMulti`. */
  isMulti: string;
  deliverKind: string;
  weightRaw: string;
  contentKindRaw: string;
};

export type PopupImportPopupForm = {
  name: string;
  /** Same as pattern form: `true` = show close button. */
  isClose: boolean;
  contentWidth: string;
  spContentWidth: string;
  colorKindRaw: string;
  title: string;
  description: string;
  button: string;
  url: string;
  isInheritQuery: string;
  hrefTargetRaw: string;
  actionKindRaw: string;
  displayOnTabFocus: string;
  displayOnMouseOut: string;
  isDisplayWhenNoOperation: string;
  customEvent: string;
  displayKindRaw: string;
  enableTapWindow: string;
  closePopupByClickingOutsideRaw: string;
  backgroundOpacity: string;
  hideByOnfocus: string;
  quitTiming: string;
  displayCountLimit: string;
  displayPopupAgain: string;
  /** Import uses URL only (`showPopupAgainImageUrl`); no file upload. */
  displayPopupAgainImage: {
    imageUrl: string;
  };
  displayPopupAgainMessage: string;
  animationKindRaw: string;
  ydaTriggerRaw: string;
  honeycombCode: string;
  statusRaw: string;
  hideByScrollPercentageRaw: string;
  /** `SCROLL_RATE_RANGE` UI + mutation `scrollRateRange` (not スクロール率制限). */
  scrollRateRangeRaw: string;
};

export type PopupImportRowAction = "" | "CREATE" | "UPDATE";

export type PopupImportRowValues = {
  /** Client-only stable id for React keys; not sent to import API. */
  _clientRowId: string;
  action: PopupImportRowAction;
  popupId: string;
  popupGroupId: string;
  popupGroupName: string;
  verificationFunnelId: string;
  /** Relay global id — select value + `importPopups` `verification.pageId`; synced with `verificationPageUrl`. */
  verificationPageId: string;
  verificationPageUrl: string;
  funnelName: string;
  group: PopupImportGroupForm;
  popup: PopupImportPopupForm;
  popupImages: PopupImportImageForm[];
};

export type PopupImportFormValues = {
  popups: PopupImportRowValues[];
};

const imageSchema = yup.object({
  imageUrl: yup.string().default(""),
  honeycombCode: yup.string().default(""),
  alt: yup.string().default(""),
  linkUrl: yup.string().default(""),
  isInheritQuery: yup.boolean().default(false),
});

const displayPopupAgainImageSchema = yup.object({
  imageUrl: yup.string().default(""),
});

export const popupImportRowSchema = yup.object({
  _clientRowId: yup.string().optional(),
  action: yup
    .string()
    .default("")
    .test(
      "popup-import-action",
      "アクションを選択してください",
      (value): value is "CREATE" | "UPDATE" =>
        value === "CREATE" || value === "UPDATE"
    ),
  popupId: yup.string().when("action", {
    is: "UPDATE",
    then: (schema) =>
      schema.required("編集の場合はポップアップIDを入力してください"),
    otherwise: (schema) => schema.default(""),
  }),
  popupGroupId: yup.string().default(""),
  popupGroupName: yup.string().default(""),
  verificationFunnelId: yup.string().default(""),
  verificationPageId: yup.string().default(""),
  verificationPageUrl: yup.string().default(""),
  funnelName: yup.string().default(""),
  group: yup
    .object({
      name: yup.string().default(""),
      memo: yup.string().default(""),
      startDate: yup.mixed<Date | null>().nullable().default(null),
      endDate: yup.mixed<Date | null>().nullable().default(null),
      scheduleIds: yup.array().of(yup.string()).default([]),
      deliverDayOfWeeks: yup
        .array()
        .of(
          yup.object({
            weekOfMonth: yup.number().nullable().default(null),
            daysOfWeek: yup.array().of(yup.string()).default([]),
          })
        )
        .default([]),
      redisplayPopupTime: yup.string().default(""),
      devices: yup
        .array()
        .of(yup.string())
        .transform((v) => {
          const a = v as string[] | undefined;
          return !a?.length ? [POPUP_IMPORT_SEGMENT_ALL_VALUE] : a;
        })
        .default(() => [POPUP_IMPORT_SEGMENT_ALL_VALUE]),
      platforms: yup
        .array()
        .of(yup.string())
        .transform((v) => {
          const a = v as string[] | undefined;
          return !a?.length ? [POPUP_IMPORT_SEGMENT_ALL_VALUE] : a;
        })
        .default(() => [POPUP_IMPORT_SEGMENT_ALL_VALUE]),
      browsers: yup
        .array()
        .of(yup.string())
        .transform((v) => {
          const a = v as string[] | undefined;
          return !a?.length ? [POPUP_IMPORT_SEGMENT_ALL_VALUE] : a;
        })
        .default(() => [POPUP_IMPORT_SEGMENT_ALL_VALUE]),
      genders: yup
        .array()
        .of(yup.string())
        .transform((v) => {
          const a = v as string[] | undefined;
          return !a?.length ? [POPUP_IMPORT_SEGMENT_ALL_VALUE] : a;
        })
        .default(() => [POPUP_IMPORT_SEGMENT_ALL_VALUE]),
      ageRanges: yup
        .array()
        .of(yup.string())
        .transform((v) => {
          const a = v as string[] | undefined;
          return !a?.length ? [POPUP_IMPORT_SEGMENT_ALL_VALUE] : a;
        })
        .default(() => [POPUP_IMPORT_SEGMENT_ALL_VALUE]),
      annualIncomes: yup
        .array()
        .of(yup.string())
        .transform((v) => {
          const a = v as string[] | undefined;
          return !a?.length ? [POPUP_IMPORT_SEGMENT_ALL_VALUE] : a;
        })
        .default(() => [POPUP_IMPORT_SEGMENT_ALL_VALUE]),
      sourceIds: yup.array().of(yup.string()).default([]),
      excludeSourceIds: yup.array().of(yup.string()).default([]),
      referrers: yup
        .array()
        .of(
          yup.object({
            referrer: yup.string().required(),
            matchOp: yup.string().required(),
          })
        )
        .default([]),
      excludeReferrers: yup
        .array()
        .of(
          yup.object({
            referrer: yup.string().required(),
            matchOp: yup.string().required(),
          })
        )
        .default([]),
      visitCount: yup.string().default(""),
      displayTargetContents: yup
        .array()
        .of(
          yup.object({
            targetKind: yup.string().required(),
            targetValue: yup.string().required(),
          })
        )
        .default([]),
      disableTargetElements: yup
        .array()
        .of(
          yup.object({
            targetKind: yup.string().required(),
            targetValue: yup.string().required(),
          })
        )
        .default([]),
      deliverTargetUrls: yup
        .array()
        .of(yup.object({ url: yup.string().required() }))
        .default([]),
      deliverExcludeUrls: yup
        .array()
        .of(yup.object({ url: yup.string().required() }))
        .default([]),
      targetCookies: yup
        .array()
        .of(
          yup.object({
            key: yup.string().required(),
            value: yup.string().default(""),
            matchType: yup.boolean().required(),
          })
        )
        .default([]),
      isMulti: yup.string().default("false"),
      deliverKind: yup.string().default("EQUAL"),
      weightRaw: yup.string().default(""),
      contentKindRaw: yup.string().default(""),
    })
    .required(),
  popup: yup
    .object({
      name: yup.string().required("ポップアップ名を入力してください"),
      isClose: yup.boolean().default(false),
      contentWidth: yup.string().default(""),
      spContentWidth: yup.string().default(""),
      colorKindRaw: popupImportEnumStringWithPatternDefault("colorKindRaw"),
      title: yup.string().default(""),
      description: yup.string().default(""),
      button: yup.string().default(""),
      url: yup.string().default(""),
      isInheritQuery: yup.string().default(""),
      hrefTargetRaw: popupImportEnumStringWithPatternDefault("hrefTargetRaw"),
      actionKindRaw: yup.string().default(""),
      displayOnTabFocus: yup.string().default(""),
      displayOnMouseOut: yup.string().default(""),
      isDisplayWhenNoOperation: yup.string().default(""),
      customEvent: yup.string().default(""),
      displayKindRaw: popupImportEnumStringWithPatternDefault("displayKindRaw"),
      enableTapWindow: yup.string().default(""),
      closePopupByClickingOutsideRaw: popupImportEnumStringWithPatternDefault(
        "closePopupByClickingOutsideRaw"
      ),
      backgroundOpacity: yup.string().default(""),
      hideByOnfocus: yup.string().default(""),
      quitTiming: yup.string().default(""),
      displayCountLimit: yup.string().default(""),
      displayPopupAgain: yup.string().default(""),
      displayPopupAgainImage: displayPopupAgainImageSchema.default({
        imageUrl: "",
      }),
      displayPopupAgainMessage: yup.string().default(""),
      animationKindRaw:
        popupImportEnumStringWithPatternDefault("animationKindRaw"),
      ydaTriggerRaw: yup.string().default(""),
      honeycombCode: yup.string().default(""),
      statusRaw: yup.string().default(""),
      hideByScrollPercentageRaw: yup.string().default(""),
      scrollRateRangeRaw: yup.string().default(""),
    })
    .required(),
  popupImages: yup.array().of(imageSchema).default([]),
});

export const validationSchema = yup.object({
  popups: yup
    .array()
    .of(popupImportRowSchema)
    .required()
    .min(1, "1件以上のポップアップを追加してください"),
});

export function normalizePopupImportFormValues(
  popups: PopupImportRowValues[] | undefined
): PopupImportRowValues[] {
  if (!popups?.length) return [];
  const normalized = popups.map((row) => {
    const cast = popupImportRowSchema.cast(row, {
      stripUnknown: true,
    }) as PopupImportRowValues;
    return {
      ...cast,
      _clientRowId: row._clientRowId ?? cast._clientRowId,
      popup: {
        ...cast.popup,
        actionKindRaw: popupImportFormActionKindRawFromCsv(
          cast.popup.actionKindRaw
        ),
      },
    };
  });
  return ensurePopupImportClientRowIds(normalized);
}
